import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import verificationService from '../api/verificationService';
import { userService } from '../api/userService';
import { useI18n, countryName } from '../i18n';

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PhoneIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ShieldCheckIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const PremiumIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.77 5.82 21l1.18-6.86-5-4.87 6.91-1.01L12 2z" />
  </svg>
);

const countryFlag = (iso) => {
  if (!iso || iso.length !== 2) return '';
  return iso.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
};

const COUNTRIES = [
  { code: '+212', country: 'Morocco', iso: 'MA' },
  { code: '+213', country: 'Algeria', iso: 'DZ' },
  { code: '+216', country: 'Tunisia', iso: 'TN' },
  { code: '+20', country: 'Egypt', iso: 'EG' },
  { code: '+966', country: 'Saudi Arabia', iso: 'SA' },
  { code: '+971', country: 'United Arab Emirates', iso: 'AE' },
  { code: '+974', country: 'Qatar', iso: 'QA' },
  { code: '+965', country: 'Kuwait', iso: 'KW' },
  { code: '+973', country: 'Bahrain', iso: 'BH' },
  { code: '+968', country: 'Oman', iso: 'OM' },
  { code: '+962', country: 'Jordan', iso: 'JO' },
  { code: '+961', country: 'Lebanon', iso: 'LB' },
  { code: '+963', country: 'Syria', iso: 'SY' },
  { code: '+964', country: 'Iraq', iso: 'IQ' },
  { code: '+970', country: 'Palestine', iso: 'PS' },
  { code: '+967', country: 'Yemen', iso: 'YE' },
  { code: '+218', country: 'Libya', iso: 'LY' },
  { code: '+249', country: 'Sudan', iso: 'SD' },
  { code: '+222', country: 'Mauritania', iso: 'MR' },
  { code: '+253', country: 'Djibouti', iso: 'DJ' },
  { code: '+252', country: 'Somalia', iso: 'SO' },
  { code: '+269', country: 'Comoros', iso: 'KM' },
  { code: '+1', country: 'United States', iso: 'US' },
  { code: '+1', country: 'Canada', iso: 'CA' },
  { code: '+44', country: 'United Kingdom', iso: 'GB' },
  { code: '+33', country: 'France', iso: 'FR' },
  { code: '+49', country: 'Germany', iso: 'DE' },
  { code: '+34', country: 'Spain', iso: 'ES' },
  { code: '+39', country: 'Italy', iso: 'IT' },
  { code: '+31', country: 'Netherlands', iso: 'NL' },
  { code: '+32', country: 'Belgium', iso: 'BE' },
  { code: '+41', country: 'Switzerland', iso: 'CH' },
  { code: '+43', country: 'Austria', iso: 'AT' },
  { code: '+46', country: 'Sweden', iso: 'SE' },
  { code: '+47', country: 'Norway', iso: 'NO' },
  { code: '+45', country: 'Denmark', iso: 'DK' },
  { code: '+351', country: 'Portugal', iso: 'PT' },
  { code: '+30', country: 'Greece', iso: 'GR' },
  { code: '+90', country: 'Turkey', iso: 'TR' },
  { code: '+7', country: 'Russia', iso: 'RU' },
  { code: '+380', country: 'Ukraine', iso: 'UA' },
  { code: '+48', country: 'Poland', iso: 'PL' },
  { code: '+420', country: 'Czech Republic', iso: 'CZ' },
  { code: '+36', country: 'Hungary', iso: 'HU' },
  { code: '+40', country: 'Romania', iso: 'RO' },
  { code: '+359', country: 'Bulgaria', iso: 'BG' },
  { code: '+381', country: 'Serbia', iso: 'RS' },
  { code: '+385', country: 'Croatia', iso: 'HR' },
  { code: '+421', country: 'Slovakia', iso: 'SK' },
  { code: '+353', country: 'Ireland', iso: 'IE' },
  { code: '+81', country: 'Japan', iso: 'JP' },
  { code: '+82', country: 'South Korea', iso: 'KR' },
  { code: '+86', country: 'China', iso: 'CN' },
  { code: '+91', country: 'India', iso: 'IN' },
  { code: '+92', country: 'Pakistan', iso: 'PK' },
  { code: '+880', country: 'Bangladesh', iso: 'BD' },
  { code: '+94', country: 'Sri Lanka', iso: 'LK' },
  { code: '+977', country: 'Nepal', iso: 'NP' },
  { code: '+66', country: 'Thailand', iso: 'TH' },
  { code: '+84', country: 'Vietnam', iso: 'VN' },
  { code: '+62', country: 'Indonesia', iso: 'ID' },
  { code: '+63', country: 'Philippines', iso: 'PH' },
  { code: '+60', country: 'Malaysia', iso: 'MY' },
  { code: '+65', country: 'Singapore', iso: 'SG' },
  { code: '+855', country: 'Cambodia', iso: 'KH' },
  { code: '+856', country: 'Laos', iso: 'LA' },
  { code: '+61', country: 'Australia', iso: 'AU' },
  { code: '+64', country: 'New Zealand', iso: 'NZ' },
  { code: '+55', country: 'Brazil', iso: 'BR' },
  { code: '+52', country: 'Mexico', iso: 'MX' },
  { code: '+54', country: 'Argentina', iso: 'AR' },
  { code: '+56', country: 'Chile', iso: 'CL' },
  { code: '+57', country: 'Colombia', iso: 'CO' },
  { code: '+51', country: 'Peru', iso: 'PE' },
  { code: '+58', country: 'Venezuela', iso: 'VE' },
  { code: '+593', country: 'Ecuador', iso: 'EC' },
  { code: '+595', country: 'Paraguay', iso: 'PY' },
  { code: '+598', country: 'Uruguay', iso: 'UY' },
  { code: '+591', country: 'Bolivia', iso: 'BO' },
  { code: '+27', country: 'South Africa', iso: 'ZA' },
  { code: '+234', country: 'Nigeria', iso: 'NG' },
  { code: '+233', country: 'Ghana', iso: 'GH' },
  { code: '+225', country: 'Cote dIvoire', iso: 'CI' },
  { code: '+221', country: 'Senegal', iso: 'SN' },
  { code: '+254', country: 'Kenya', iso: 'KE' },
  { code: '+255', country: 'Tanzania', iso: 'TZ' },
  { code: '+256', country: 'Uganda', iso: 'UG' },
  { code: '+250', country: 'Rwanda', iso: 'RW' },
  { code: '+251', country: 'Ethiopia', iso: 'ET' },
  { code: '+237', country: 'Cameroon', iso: 'CM' },
  { code: '+243', country: 'DR Congo', iso: 'CD' },
  { code: '+242', country: 'Congo', iso: 'CG' },
  { code: '+228', country: 'Togo', iso: 'TG' },
  { code: '+229', country: 'Benin', iso: 'BJ' },
  { code: '+226', country: 'Burkina Faso', iso: 'BF' },
  { code: '+227', country: 'Niger', iso: 'NE' },
  { code: '+223', country: 'Mali', iso: 'ML' },
  { code: '+220', country: 'Gambia', iso: 'GM' },
  { code: '+224', country: 'Guinea', iso: 'GN' },
  { code: '+231', country: 'Liberia', iso: 'LR' },
  { code: '+232', country: 'Sierra Leone', iso: 'SL' },
  { code: '+244', country: 'Angola', iso: 'AO' },
  { code: '+260', country: 'Zambia', iso: 'ZM' },
  { code: '+263', country: 'Zimbabwe', iso: 'ZW' },
  { code: '+267', country: 'Botswana', iso: 'BW' },
  { code: '+264', country: 'Namibia', iso: 'NA' },
  { code: '+268', country: 'Eswatini', iso: 'SZ' },
  { code: '+266', country: 'Lesotho', iso: 'LS' },
  { code: '+230', country: 'Mauritius', iso: 'MU' },
  { code: '+261', country: 'Madagascar', iso: 'MG' },
  { code: '+258', country: 'Mozambique', iso: 'MZ' },
  { code: '+972', country: 'Israel', iso: 'IL' },
  { code: '+98', country: 'Iran', iso: 'IR' },
  { code: '+93', country: 'Afghanistan', iso: 'AF' },
  { code: '+994', country: 'Azerbaijan', iso: 'AZ' },
  { code: '+374', country: 'Armenia', iso: 'AM' },
  { code: '+995', country: 'Georgia', iso: 'GE' },
  { code: '+998', country: 'Uzbekistan', iso: 'UZ' },
  { code: '+996', country: 'Kyrgyzstan', iso: 'KG' },
  { code: '+992', country: 'Tajikistan', iso: 'TJ' },
  { code: '+993', country: 'Turkmenistan', iso: 'TM' },
  { code: '+357', country: 'Cyprus', iso: 'CY' },
  { code: '+356', country: 'Malta', iso: 'MT' },
  { code: '+389', country: 'North Macedonia', iso: 'MK' },
  { code: '+355', country: 'Albania', iso: 'AL' }
];

const PhoneVerificationModal = ({ isOpen, onClose, onVerified, onUpgrade, phoneNumber: initialPhoneNumber }) => {
  const { t, lang, isRTL } = useI18n();
  const [screen, setScreen] = useState('choice');
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || '');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [localNumber, setLocalNumber] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [rateLimited, setRateLimited] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [geoDetected, setGeoDetected] = useState(false);

  const otpInputsRef = useRef([]);
  const dropdownRef = useRef(null);
  const countryButtonRef = useRef(null);

  // Detect user's country via geo-IP and set default country code
  useEffect(() => {
    if (geoDetected || initialPhoneNumber) return;
    fetch('https://api.country.is/')
      .then((res) => res.json())
      .then((data) => {
        if (data?.country) {
          const match = COUNTRIES.find((c) => c.iso === data.country);
          if (match) setSelectedCountry(match);
        }
      })
      .catch(() => {})
      .finally(() => setGeoDetected(true));
  }, [geoDetected, initialPhoneNumber]);

  const normalizedPhoneNumber = useMemo(() => {
    if (!localNumber) return '';
    // Strip leading 0 if present (common in many countries)
    return localNumber.startsWith('0') ? localNumber.slice(1) : localNumber;
  }, [localNumber]);

  const isPhoneValid = useMemo(
    () => normalizedPhoneNumber.length >= 4 && normalizedPhoneNumber.length <= 15,
    [normalizedPhoneNumber]
  );

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return COUNTRIES;
    const q = countrySearch.toLowerCase();
    return COUNTRIES.filter((c) => c.country.toLowerCase().includes(q) || countryName(c.iso, c.country, lang).toLowerCase().includes(q) || c.code.includes(countrySearch));
  }, [countrySearch, lang]);

  useEffect(() => {
    if (!initialPhoneNumber) return;
    const trimmed = String(initialPhoneNumber).trim();
    setPhoneNumber(trimmed);

    const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
    const match = sorted.find((item) => trimmed.startsWith(item.code));
    if (match) {
      setSelectedCountry(match);
      setLocalNumber(trimmed.replace(match.code, '').trim().replace(/\D/g, ''));
    }
  }, [initialPhoneNumber]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
    setResendDisabled(false);
    return undefined;
  }, [resendCountdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        countryButtonRef.current && !countryButtonRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const maskPhoneNumber = useCallback((phone) => {
    const match = phone.match(/^(\+\d+)\s?(\d{1,3})(\d*)(\d{3})$/);
    if (!match) return phone;
    const [, code, start, middle, end] = match;
    return `${code} ${start}${middle.replace(/\d/g, 'X')}${end}`;
  }, []);

  const handleSendOTP = async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await verificationService.sendOTP();
      if (result?.success) {
        setScreen('otp');
        setResendDisabled(true);
        setResendCountdown(60);
        if (result.remaining_attempts != null && result.remaining_attempts <= 0) {
          setRateLimited(true);
        }
      } else {
        setError(result?.message || 'Failed to send OTP');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setRateLimited(true);
        setScreen('otp');
        setError('You have used all your resend attempts. Please contact support for help.');
      } else {
        setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePhone = async () => {
    if (!isPhoneValid) {
      setPhoneError(t('Please enter a valid phone number.'));
      return;
    }

    setIsLoading(true);
    setError('');
    setPhoneError('');

    const fullPhone = `${selectedCountry.code} ${normalizedPhoneNumber}`;
    try {
      await userService.updatePhoneNumber(fullPhone);
      setPhoneNumber(fullPhone);
      setScreen('choice');
      setShowCountryDropdown(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update phone number');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (providedCode = null) => {
    const code = providedCode ?? otpCode.join('');
    if (code.length !== 6) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await verificationService.verifyOTP(code);
      if (result?.success) {
        onVerified();
        onClose();
      } else {
        setError(result?.message || 'Invalid OTP code');
        setOtpCode(['', '', '', '', '', '']);
        otpInputsRef.current[0]?.focus();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP code');
      setOtpCode(['', '', '', '', '', '']);
      otpInputsRef.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    const clean = value.replace(/\D/g, '').slice(0, 1);
    const next = [...otpCode];
    next[index] = clean;
    setOtpCode(next);

    if (clean && index < 5) otpInputsRef.current[index + 1]?.focus();
    if (next.every((digit) => digit) && index === 5) {
      setTimeout(() => handleVerifyOTP(next.join('')), 50);
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otpCode];
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setOtpCode(next);
    if (pasted.length === 6) setTimeout(() => handleVerifyOTP(next.join('')), 50);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-[slideUp_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 end-4 z-10 p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <XIcon />
        </button>

        <div className="px-5 sm:px-8 py-6 sm:py-8">
          {screen === 'choice' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#f73140]/10 text-[#f73140] mb-4">
                  <PhoneIcon size={30} />
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900 mb-2">{t('To Export for Free, Verify Your Number')}</h2>
                <p className="text-slate-500 text-sm sm:text-base leading-relaxed">
                  {t('To export for free, please verify your number on WhatsApp. You can also skip verification and upgrade your plan.')}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">{t('Phone Number')}</p>
                    <p className="text-base sm:text-lg font-bold text-slate-900 break-all" dir={phoneNumber ? 'ltr' : undefined}>{phoneNumber || t('Not set')}</p>
                  </div>
                  <button
                    onClick={() => setScreen('edit_phone')}
                    className="px-3 py-2 text-sm font-bold text-[#f73140] hover:bg-[#f73140]/10 rounded-xl transition-colors"
                  >
                    {t('Edit')}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700">{t(error)}</p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleSendOTP}
                  disabled={isLoading || !phoneNumber}
                  className="w-full py-3.5 px-6 bg-[#f73140] text-white font-bold rounded-2xl hover:bg-[#d62331] transition-all shadow-lg shadow-[#f73140]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('Sending...')}
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon size={20} />
                      {t('Verify Number')}
                    </>
                  )}
                </button>

                <button
                  onClick={onUpgrade}
                  className="w-full py-3.5 px-6 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 flex items-center justify-center gap-2"
                >
                  <PremiumIcon size={20} />
                  {t('Skip Verification & Upgrade')}
                </button>
              </div>
            </div>
          )}

          {screen === 'edit_phone' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-extrabold text-slate-900 mb-2">{t('Update Phone Number')}</h2>
                <p className="text-slate-500">{t('Same style as onboarding, optimized for mobile.')}</p>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <button
                    ref={countryButtonRef}
                    type="button"
                    onClick={() => setShowCountryDropdown((prev) => !prev)}
                    className="w-full h-14 px-4 rounded-2xl border border-slate-200 bg-white text-start flex items-center justify-between hover:border-[#f73140]/40 transition-colors"
                  >
                    <span className="font-semibold text-slate-900 truncate">
                      {countryFlag(selectedCountry.iso)} <bdi dir="ltr">{selectedCountry.code}</bdi> {countryName(selectedCountry.iso, selectedCountry.country, lang)}
                    </span>
                    <span className="text-slate-400">▼</span>
                  </button>

                  {showCountryDropdown && createPortal(
                    <div
                      ref={dropdownRef}
                      className="fixed z-[10000] bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[52vh] overflow-hidden flex flex-col"
                      style={{
                        width: countryButtonRef.current?.offsetWidth || 400,
                        top: (countryButtonRef.current?.getBoundingClientRect().bottom || 200) + 8,
                        ...(isRTL
                          ? { right: window.innerWidth - (countryButtonRef.current?.getBoundingClientRect().right || window.innerWidth) }
                          : { left: countryButtonRef.current?.getBoundingClientRect().left || 0 }),
                      }}
                    >
                      <div className="p-3 border-b border-slate-100">
                        <input
                          type="text"
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          placeholder={t('Search country or code...')}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#f73140] focus:ring-2 focus:ring-[#f73140]/10"
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto p-2">
                        {filteredCountries.map((country) => (
                          <button
                            key={`${country.iso}-${country.code}-${country.country}`}
                            type="button"
                            onClick={() => {
                              setSelectedCountry(country);
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                            }}
                            className={`w-full px-3 py-2 rounded-xl text-start text-sm transition-colors ${
                              selectedCountry.iso === country.iso && selectedCountry.code === country.code
                                ? 'bg-[#f73140]/10 text-[#f73140] font-semibold'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            {countryFlag(country.iso)} <bdi dir="ltr">{country.code}</bdi> {countryName(country.iso, country.country, lang)}
                          </button>
                        ))}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>

                <div>
                  <input
                    type="tel"
                    value={localNumber}
                    onChange={(e) => {
                      setLocalNumber(e.target.value.replace(/\D/g, '').slice(0, 15));
                      if (phoneError) setPhoneError('');
                    }}
                    placeholder={t('Phone number')}
                    className={`w-full h-14 px-4 bg-white border rounded-2xl text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#f73140]/20 ${
                      phoneError ? 'border-red-300' : 'border-slate-200 focus:border-[#f73140]'
                    }`}
                  />
                  <p className="text-xs text-slate-400 mt-2">{t('Enter your number with or without leading 0')}</p>
                </div>
              </div>

              {phoneError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700">{t(phoneError)}</p>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700">{t(error)}</p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleUpdatePhone}
                  disabled={isLoading || !localNumber}
                  className="w-full py-3.5 px-6 bg-[#f73140] text-white font-bold rounded-2xl hover:bg-[#d62331] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t(isLoading ? 'Updating...' : 'Update Number')}
                </button>

                <button
                  onClick={() => {
                    setScreen('choice');
                    setError('');
                    setPhoneError('');
                  }}
                  className="w-full py-3.5 px-6 bg-slate-100 text-slate-700 font-bold rounded-2xl hover:bg-slate-200 transition-colors"
                >
                  {t('Cancel')}
                </button>
              </div>
            </div>
          )}

          {screen === 'otp' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#f73140]/10 text-[#f73140] mb-4">
                  <ShieldCheckIcon size={30} />
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900 mb-2">{t('Enter Verification Code')}</h2>
                <p className="text-slate-500 mb-1">{t('We sent a 6-digit code via WhatsApp to')}</p>
                <p className="text-base sm:text-lg font-bold text-slate-900" dir="ltr">{maskPhoneNumber(phoneNumber)}</p>
              </div>

              <div className="flex justify-center gap-2 sm:gap-3" dir="ltr">
                {otpCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      otpInputsRef.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(index, e)}
                    onPaste={index === 0 ? handleOTPPaste : undefined}
                    className="w-10 sm:w-12 h-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-[#f73140] focus:outline-none transition-colors"
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700">{t(error)}</p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleVerifyOTP}
                  disabled={isLoading || otpCode.some((d) => !d)}
                  className="w-full py-3.5 px-6 bg-[#f73140] text-white font-bold rounded-2xl hover:bg-[#d62331] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t(isLoading ? 'Verifying...' : 'Verify Code')}
                </button>

                {!rateLimited && (
                  <button
                    onClick={handleSendOTP}
                    disabled={resendDisabled || isLoading}
                    className="w-full py-3.5 px-6 text-[#f73140] font-bold hover:bg-[#f73140]/10 rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendDisabled ? t('Resend in {seconds}s', { seconds: resendCountdown }) : t('Resend Code')}
                  </button>
                )}

                {rateLimited && (
                  <a
                    href="https://wa.me/212691154252?text=Hi,%20I%20need%20help%20with%20phone%20verification.%20I've%20used%20all%20my%20OTP%20attempts."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3.5 px-6 bg-[#25D366] text-white font-bold rounded-2xl hover:bg-[#128C7E] transition-colors flex items-center justify-center gap-2"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {t('Contact Support on WhatsApp')}
                  </a>
                )}

                <button
                  onClick={onUpgrade}
                  className="w-full py-3.5 px-6 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 flex items-center justify-center gap-2"
                >
                  <PremiumIcon size={20} />
                  {t('Skip Verification & Upgrade')}
                </button>

                <button
                  onClick={() => {
                    setScreen('choice');
                    setOtpCode(['', '', '', '', '', '']);
                    setError('');
                  }}
                  className="w-full py-3.5 px-6 bg-slate-100 text-slate-700 font-bold rounded-2xl hover:bg-slate-200 transition-colors"
                >
                  {t('Back')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhoneVerificationModal;
